import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { Combobox, ComboboxProps } from "./base";

/**
 * react-hook-form stores a multi-select as `string[]` and a single-select as
 * `string`; the {@link Combobox} always works in arrays, so normalize on the
 * way in and collapse back to the field's shape on the way out.
 */
function toSelectionArray(fieldValue: unknown, multiple: boolean): string[] {
  if (multiple) return Array.isArray(fieldValue) ? fieldValue : [];
  return fieldValue ? [String(fieldValue)] : [];
}

function fromSelectionArray(
  selection: string[],
  multiple: boolean,
): string[] | string {
  return multiple ? selection : (selection[selection.length - 1] ?? "");
}

export interface ControlledComboboxProps extends Omit<
  ComboboxProps,
  "value" | "onChange" | "onBlur" | "error" | "required" | "inputRef"
> {
  /** Field name registered on the surrounding react-hook-form context. */
  name: string;
  /** Read the error from a different field than `name` (e.g. a grouped error). */
  errorName?: string;
  rules?: {
    required?: { value: boolean; message: string };
    validate?: (
      val: string | string[],
    ) => string | boolean | undefined | Promise<string | boolean | undefined>;
  };
}

/**
 * react-hook-form–bound {@link Combobox}: the form owns the selection and
 * validation via `name`/`rules`. In multi-select the field value is a
 * `string[]`; in single-select it is a `string`. Reach for this inside a form;
 * use the plain controlled `Combobox` when a non-form parent owns the value, or
 * `UncontrolledCombobox` when the field is self-contained.
 */
export function ControlledCombobox({
  name,
  errorName = "",
  rules = {},
  disabled = false,
  multiple = false,
  ...rest
}: ControlledComboboxProps) {
  const { control, getFieldState } = useFormContext();
  const { field, fieldState } = useController({ name, control, rules });
  const required = rules.required?.value === true;

  const { error } = useMemo(
    () => getFieldState(errorName ? errorName : name),
    [getFieldState, errorName, name, fieldState.error],
  );

  return (
    <Combobox
      {...rest}
      multiple={multiple}
      name={field.name}
      value={toSelectionArray(field.value, multiple)}
      onChange={(next) => field.onChange(fromSelectionArray(next, multiple))}
      onBlur={field.onBlur}
      inputRef={field.ref}
      disabled={field.disabled || disabled}
      required={required}
      error={error?.message}
    />
  );
}

export default ControlledCombobox;
